import React, { useState, useEffect } from 'react';

import { userFinalize } from '../../api';
import { DefaultResponse, API_STATUS } from '../../types';
import { DefaultComponentProps, MessageCodes, CreateUserResultTypes, FormStatus } from '../../../types';
import { isEmpty, isOnlyNumbers, isSpecificLength } from '../../../validation';
import { ariaInvalid } from '../../../helpers';

import { CardHeader } from '../../../components/CardHeader';
import { CardBody } from '../../../components/CardBody';
import { Spinner } from '../../../components';

import './style.scss';

interface Props extends DefaultComponentProps {
  flowKey: string;
  setView: (resultType: CreateUserResultTypes) => void;
  userFinalizedSuccess: () => void;
}

export const Offcode = ({ textConfig, flowKey, setView: userFinalizeFailed, userFinalizedSuccess }: Props) => {
  const { title, ingress, emoji, emojiLabel, offcodeLabel, buttonSubmitText, formValidationFeedback } = textConfig.offCode;
  const [offcode, setOffcode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formStatus, setFormStatus] = useState({
    valid: false,
    status: FormStatus.INITIAL,
  });
  const [validationFeedback, setValidationFeedback] = useState({
    offcode: formValidationFeedback.offcode,
  });

  useEffect(() => {
    setFormStatus(state => ({
      ...state,
      valid: (Object.values(validationFeedback).every((feedback) => feedback === '')),
    }));
  }, [validationFeedback, formStatus, formStatus.valid]);

  const handleOffcode = ({ currentTarget }: React.SyntheticEvent<HTMLInputElement>) => {
    const { value } = currentTarget;
    setOffcode(value);
    setFormStatus(state => ({
      ...state,
      status: FormStatus.INITIAL,
    }));

    if (!isEmpty(value) && isOnlyNumbers(value) && isSpecificLength(value, 5)) {
      setValidationFeedback(state => ({ ...state, offcode: '' }));
    } else {
      setValidationFeedback(state => ({ ...state, offcode: formValidationFeedback.offcode }));
    }
  };

  const handleFormSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formStatus.valid) {
      setFormStatus(state => ({ ...state, status: FormStatus.TRIED_SUBMITTING }));
      return;
    }
    setFormStatus(state => ({ ...state, status: FormStatus.SUBMITTING }));

    userFinalize({ flowKey, offcode })
      .then((response: DefaultResponse) => {
        switch (response.status) {
          case API_STATUS.NOT_AUTHENTICATED:
          case API_STATUS.FAILURE:
            if (response.messageCode === MessageCodes.INVALID_OFFCODE_6008) {
              setErrorMessage(textConfig.offCode.invalidOffcode);
              setFormStatus(state => ({ ...state, status: FormStatus.TRIED_SUBMITTING }));
            } else {
              console.warn(response);
              userFinalizeFailed(CreateUserResultTypes.USER_CREATE_FAILED);
            }
            break;
          case API_STATUS.SUCCESS:
            userFinalizedSuccess();
            break;
        }
      })
      .catch(err => {
        console.warn(err);
        userFinalizeFailed(CreateUserResultTypes.SYSTEM_ERROR);
      });
  };

  const showOffcodeValidation = formStatus.status === FormStatus.TRIED_SUBMITTING && validationFeedback.offcode !== '';

  return (
    <>
    {formStatus.status === FormStatus.SUBMITTING ? <Spinner textConfig={textConfig} /> :
      (
      <>
      <CardHeader title={title} ingress={ingress} emoji={emoji} emojiLabel={emojiLabel} />
      <CardBody>
        <form onSubmit={handleFormSubmit}>
          <div className="form__field">
            <label htmlFor="offcode" className="form__field__label">
            {offcodeLabel}
            </label>
            <input
                onChange={handleOffcode}
                type="text"
                pattern="[0-9]*"
                inputMode="numeric"
                id="offcode"
                className="form__field__input form__field__input__offcode"
                aria-describedby="offcodeFeedback"
                aria-invalid={ariaInvalid(formStatus.status, validationFeedback.offcode)}
                value={offcode}
                maxLength={5}
                autoFocus={true}
            />
            <div className="form__field__feedback">
              {showOffcodeValidation && <div className="form__field__feedback__text" id="offcodeFeedback">{validationFeedback.offcode}</div>}
            </div>
          </div>

          <div className="form__field">
            <div className="form__field__submit">
            <button type="submit" id="wp-submit-pending-user" className="form__field__submit__button">
              {buttonSubmitText}
            </button>
            </div>
          </div>
        </form>
      </CardBody>
      {errorMessage !== '' && <div className="alert alert-danger form__offcode__feedback" role="alert">{errorMessage}</div>}
      </>
      )
    }
    </>
  );
};
